import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import roverImg from "../assests/rover_custom.png";
import jetsonImg from "../assests/jetson_custom.png";
import piImg from "../assests/pi_custom.png";
import droneImg from "../assests/drone2_custom.png";
import "./Hero.css";

function Hero({ islogin, setUserSidebar }) {
  const [activeTab, setActiveTab] = useState(0);

  // Preload all hardware images on mount so there is ZERO decoding lag when switching slides
  useEffect(() => {
    const imagesToPreload = [roverImg, droneImg, jetsonImg, piImg];
    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Automatic Tab Switching (Ultra smooth interval)
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prevTab) => (prevTab + 1) % 4);
    }, 5000); // 5 seconds per slide for a relaxed, cinematic viewing experience

    return () => clearInterval(timer);
  }, []);

  // 3D Tilt Mouse Tracking for the Right Stage image
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useTransform(y, [-300, 300], [12, -12]);
  const rotateY = useTransform(x, [-300, 300], [-12, 12]);

  function handleMouseMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = event.clientX - rect.left - width / 2;
    const mouseY = event.clientY - rect.top - height / 2;
    x.set(mouseX);
    y.set(mouseY);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  // Exact Vicharak Style Data (Pitch black cinematic theme)
  const showcaseItems = [
    {
      id: "rover",
      category: "ROBOTICS",
      title: "AUTONOMOUS ROVER",
      subtitle: "Conquering rough terrain with autonomous precision. Armed with 360° LiDAR navigation and automated smart spraying, our unmanned rovers patrol and protect your land 24/7.",
      image: roverImg,
      glowColor: "rgba(239, 68, 68, 0.35)", // Dramatic Red/Coral Glow
      accentColor: "#ef4444"
    },
    {
      id: "drone",
      category: "DRONES & AERIAL",
      title: "AERIAL DRONES",
      subtitle: "Eyes in the sky. Intelligence on the ground. Our multi-spectral autonomous drones scan thousands of acres in minutes, detecting crop stress weeks before visible symptoms appear.",
      image: droneImg,
      glowColor: "rgba(16, 185, 129, 0.35)", // Emerald Neon Glow
      accentColor: "#10b981"
    },
    {
      id: "jetson",
      category: "EDGE COMPUTING",
      title: "JETSON NANO AI",
      subtitle: "Supercomputing at the edge. Powered by NVIDIA Jetson Nano, our onboard AI engines process 472 GFLOPS of neural vision data in real time—delivering split-second autonomous decisions.",
      image: jetsonImg,
      glowColor: "rgba(168, 85, 247, 0.4)", // Purple/Pink Sci-Fi Glow
      accentColor: "#a855f7"
    },
    {
      id: "pi",
      category: "IOT TELEMETRY",
      title: "RASPBERRY PI IOT",
      subtitle: "The pulse of connected agriculture. Custom telemetry modules engineered for instant 4G/5G cloud synchronization, automated irrigation control, and real-time field diagnostics.",
      image: piImg,
      glowColor: "rgba(245, 158, 11, 0.35)", // Golden Circuit Glow
      accentColor: "#f59e0b"
    }
  ];

  const current = showcaseItems[activeTab];

  // Helper to render custom GPU-accelerated smooth animations per hardware type
  const renderCustomHardwareAnimation = () => {
    if (current.id === "drone") {
      return (
        <div className="relative w-full h-full flex items-center justify-center will-change-transform">
          {/* Ground Radar Ring underneath drone */}
          <motion.div
            animate={{ scale: [0.85, 1.3, 0.85], opacity: [0.5, 0.1, 0.5], rotateZ: [0, 360] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
            className="absolute bottom-10 w-64 h-64 md:w-80 md:h-80 rounded-full border-2 border-dashed border-emerald-400/30 pointer-events-none will-change-transform"
            style={{ transform: "rotateX(70deg)" }}
          ></motion.div>

          {/* Actually Flying Drone Image */}
          <motion.img
            animate={{ 
              y: [-15, 15, -15], 
              rotateZ: [-2, 2, -2]
            }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            src={current.image}
            alt={current.title}
            className="max-h-[380px] sm:max-h-[460px] lg:max-h-[520px] w-auto object-contain drop-shadow-[0_25px_35px_rgba(0,0,0,0.8)] relative z-10 will-change-transform"
          />
        </div>
      );
    } 
    
    if (current.id === "rover") {
      return (
        <div className="relative w-full h-full flex items-center justify-center will-change-transform">
          {/* Ground Terrain Track Shadow */}
          <motion.div 
            animate={{ scaleX: [0.95, 1.05, 0.95], opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            className="absolute bottom-12 w-72 h-12 bg-red-950/40 rounded-full blur-xl pointer-events-none will-change-transform"
          ></motion.div>

          {/* Actually Driving Rover Image */}
          <motion.img
            animate={{ 
              x: [-10, 10, -10], 
              y: [-2, 2, -2]
            }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            src={current.image}
            alt={current.title}
            className="max-h-[380px] sm:max-h-[460px] lg:max-h-[520px] w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.85)] relative z-10 will-change-transform"
          />

          {/* LiDAR Laser Beam Scanning Forward from Rover */}
          <motion.div
            animate={{ left: ["15%", "85%", "15%"], opacity: [0, 0.8, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            className="absolute bottom-1/3 h-0.5 w-28 bg-red-500 shadow-[0_0_15px_#ef4444] pointer-events-none z-20 will-change-transform"
          ></motion.div>
        </div>
      );
    } 
    
    if (current.id === "jetson") {
      return (
        <div className="relative w-full h-full flex items-center justify-center will-change-transform">
          {/* AI Neural Network Pulse Rings */}
          <motion.div
            animate={{ scale: [0.9, 1.2, 0.9], opacity: [0.2, 0.4, 0.2], rotateZ: [360, 0] }}
            transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
            className="absolute w-72 h-72 rounded-full border border-purple-500/25 border-dashed pointer-events-none will-change-transform"
          ></motion.div>

          {/* Actually Levitating Jetson Motherboard */}
          <motion.img
            animate={{ 
              y: [-10, 10, -10], 
              rotateZ: [-1, 1, -1] 
            }}
            transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
            src={current.image}
            alt={current.title}
            className="max-h-[380px] sm:max-h-[460px] lg:max-h-[520px] w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.85)] relative z-10 will-change-transform"
          />
        </div>
      );
    }

    return (
      <div className="relative w-full h-full flex items-center justify-center will-change-transform">
        {/* Golden Telemetry Sync Rings */}
        <motion.div
          animate={{ scale: [1, 1.4], opacity: [0.5, 0] }}
          transition={{ repeat: Infinity, duration: 2.5, ease: "easeOut" }}
          className="absolute w-60 h-60 rounded-full border border-amber-500/30 pointer-events-none will-change-transform"
        ></motion.div>

        {/* Oscillating Telemetry Board */}
        <motion.img
          animate={{ 
            y: [-8, 8, -8]
          }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          src={current.image}
          alt={current.title}
          className="max-h-[380px] sm:max-h-[460px] lg:max-h-[520px] w-auto object-contain drop-shadow-[0_25px_40px_rgba(0,0,0,0.85)] relative z-10 will-change-transform"
        />
      </div>
    );
  };

  return (
    <div className="bg-[#000000] text-white min-h-screen pt-24 pb-16 flex flex-col justify-between relative overflow-hidden font-sans">
      
      {/* Smoothly Transitioning Background Glow */}
      <AnimatePresence mode="wait">
        <motion.div 
          key={current.id + "-glow"}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.1 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="absolute w-[600px] h-[600px] md:w-[800px] md:h-[800px] rounded-full blur-[180px] pointer-events-none will-change-transform"
          style={{ background: current.glowColor, top: "20%", right: "5%" }}
        ></motion.div>
      </AnimatePresence>

      {/* Subtle top spotlight */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[300px] bg-white/[0.03] blur-[120px] pointer-events-none"></div>

      {/* MAIN HERO CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex-1 flex items-center relative z-10 my-auto py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center w-full">
          
          {/* LEFT COLUMN: Typography & Prompt Layout */}
          <div className="lg:col-span-6 flex flex-col justify-between z-20">
            
            <div className="w-full">
              {/* Title with Smooth Shimmer */}
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-8 font-sans flex flex-wrap gap-x-4">
                <span className="text-white drop-shadow-lg">NXV</span>
                <span 
                  className="text-transparent bg-clip-text transition-all duration-700 font-extrabold"
                  style={{
                    backgroundImage: `linear-gradient(to right, #ffffff, ${current.accentColor})`,
                    textShadow: `0 0 45px ${current.glowColor}`
                  }}
                >
                  DYNAMICS
                </span>
              </h1>

              {/* ULTRA SMOOTH AnimatePresence for Subtitle */}
              <div className="min-h-[140px] mb-10">
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={current.id + "-sub"}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="relative bg-gradient-to-r from-white/[0.07] via-white/[0.03] to-transparent p-6 rounded-r-2xl border-l-4 max-w-xl backdrop-blur-md shadow-2xl transition-all duration-500 hover:from-white/[0.1]"
                    style={{ borderLeftColor: current.accentColor }}
                  >
                    <p className="text-base sm:text-lg text-gray-100 font-normal leading-relaxed tracking-wide">
                      {current.subtitle}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-5 mb-14">
                <Link
                  to={islogin ? "#" : "/login"}
                  onClick={islogin ? () => setUserSidebar(true) : undefined}
                  className="group px-8 py-4 rounded-xl bg-white text-black font-extrabold text-sm hover:bg-gray-100 transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:scale-105 flex items-center gap-2"
                >
                  <span>{islogin ? "Dashboard Profile" : "Explore Hardware"}</span>
                  <span className="group-hover:translate-x-1 transition-transform font-black">&rarr;</span>
                </Link>
                <Link 
                  to="/about" 
                  className="px-8 py-4 rounded-xl bg-white/[0.05] border border-white/20 text-white font-bold text-sm hover:bg-white/[0.15] hover:border-white/40 transition-all duration-300 backdrop-blur-md"
                >
                  System Specs
                </Link>
              </div>
            </div>

            {/* Bottom Left: Category Indicator with Smooth Transition */}
            <div className="pt-4 flex items-center gap-4">
              <div className="w-16 sm:w-24 h-[2px] bg-white"></div>
              <AnimatePresence mode="wait">
                <motion.span 
                  key={current.id + "-cat"}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.3 }}
                  className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-widest text-white uppercase font-sans"
                >
                  {current.category}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* Manual Slide Selector Pills for Ultra Smooth User Control */}
            <div className="flex items-center gap-2 mt-6">
              {showcaseItems.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(idx)}
                  className={`h-2 rounded-full transition-all duration-500 ${
                    activeTab === idx ? "w-10 bg-white" : "w-2 bg-white/30 hover:bg-white/60"
                  }`}
                  style={{ backgroundColor: activeTab === idx ? current.accentColor : undefined }}
                  aria-label={`Switch to ${item.title}`}
                ></button>
              ))}
            </div>

          </div>

          {/* RIGHT COLUMN: Ultra-Smooth AnimatePresence 3D Stage */}
          <div className="lg:col-span-6 flex items-center justify-center min-h-[450px] sm:min-h-[550px] relative">
            
            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className="w-full h-full flex items-center justify-center cursor-pointer relative p-4"
            >
              <AnimatePresence mode="wait">
                <motion.div 
                  key={current.id}
                  initial={{ opacity: 0, scale: 0.92, y: 15 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="relative z-10 w-full h-full flex items-center justify-center will-change-transform"
                  style={{ transform: "translateZ(50px)" }}
                >
                  {renderCustomHardwareAnimation()}
                </motion.div>
              </AnimatePresence>
            </motion.div>

          </div>

        </div>
      </div>

    </div>
  );
}

export default Hero;
